/**
 * Content Manager - Templates Service
 *
 * API service for managing content templates.
 */

import type { ContentTemplate, ApiResponse } from '../types';
import { logger } from '../lib/logger';

// =============================================================================
// DEFAULT TEMPLATES
// =============================================================================

const DEFAULT_SEED_TEMPLATES: ContentTemplate[] = [
  {
    id: 'tpl-seed-product-launch',
    name: 'Product Launch Campaign',
    description: 'Complete campaign template for launching a new product or feature',
    type: 'seed',
    category: 'Marketing',
    isDefault: true,
    data: {
      title: 'Product Launch: [Product Name]',
      description: 'Strategic campaign for launching [Product Name]. Includes awareness, consideration, and conversion phases.',
      priority: 'High',
      tags: ['launch', 'product', 'campaign'],
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-seed-content-series',
    name: 'Content Series',
    description: 'Template for creating a multi-part content series',
    type: 'seed',
    category: 'Content',
    isDefault: true,
    data: {
      title: '[Topic] Series',
      description: 'A comprehensive series exploring [topic] across multiple content pieces.',
      priority: 'Medium',
      tags: ['series', 'educational'],
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-seed-event',
    name: 'Event Campaign',
    description: 'Campaign template for promoting events, webinars, or conferences',
    type: 'seed',
    category: 'Events',
    isDefault: true,
    data: {
      title: '[Event Name] Campaign',
      description: 'Pre-event promotion, live coverage, and post-event follow-up content.',
      priority: 'High',
      tags: ['event', 'webinar', 'promotion'],
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-seed-thought-leadership',
    name: 'Thought Leadership',
    description: 'Establish industry authority with thought leadership content',
    type: 'seed',
    category: 'Brand',
    isDefault: true,
    data: {
      title: 'Thought Leadership: [Topic]',
      description: 'Position our brand as an industry leader on [topic].',
      priority: 'Medium',
      tags: ['thought-leadership', 'authority', 'brand'],
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const DEFAULT_OUTPUT_TEMPLATES: ContentTemplate[] = [
  {
    id: 'tpl-output-blog-post',
    name: 'Blog Post',
    description: 'Standard blog post with SEO optimization',
    type: 'output',
    contentType: 'Article',
    category: 'Written',
    isDefault: true,
    data: {
      contentType: 'Article',
      platforms: ['Website'],
      content: `# [Title]

## Introduction
[Hook your readers with a compelling opening]

## Key Points

### Point 1
[Explain your first main point]

### Point 2
[Explain your second main point]

### Point 3
[Explain your third main point]

## Conclusion
[Summarize and include a call-to-action]
`,
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-output-linkedin-post',
    name: 'LinkedIn Post',
    description: 'Engaging LinkedIn post with best practices',
    type: 'output',
    contentType: 'Social Post',
    category: 'Social',
    isDefault: true,
    data: {
      contentType: 'Social Post',
      platforms: ['LinkedIn'],
      socialCopy: `🎯 [Attention-grabbing hook]

Here's what I learned about [topic]:

1️⃣ [First insight]
2️⃣ [Second insight]
3️⃣ [Third insight]

The key takeaway? [Main message]

💡 What's your experience with [topic]?

#ContentMarketing #[Industry] #[Topic]`,
      hashtags: '#ContentMarketing #Marketing #Growth',
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-output-twitter-thread',
    name: 'Twitter/X Thread',
    description: 'Engaging thread format for Twitter/X',
    type: 'output',
    contentType: 'Social Post',
    category: 'Social',
    isDefault: true,
    data: {
      contentType: 'Social Post',
      platforms: ['Twitter'],
      socialCopy: `🧵 Thread: [Topic]

1/ [Opening hook - make it compelling]

2/ [First point with context]

3/ [Second point - add value]

4/ [Third point - be specific]

5/ [Actionable takeaway]

6/ [Call to action + retweet request]

Follow for more [topic] insights!`,
      hashtags: '#Thread #Marketing',
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-output-email-newsletter',
    name: 'Email Newsletter',
    description: 'Newsletter template with sections for news, tips, and CTA',
    type: 'output',
    contentType: 'Newsletter',
    category: 'Email',
    isDefault: true,
    data: {
      contentType: 'Newsletter',
      platforms: ['Email'],
      content: `# [Newsletter Title]

Hey [First Name],

[Personal greeting or timely hook]

## 📰 This Week's Highlights

### [Main Story]
[Summary of the main piece]

[Read more →]

### [Secondary Story]
[Brief summary]

## 💡 Quick Tip
[Actionable tip your readers can use immediately]

## 🔗 Worth Reading
- [Link 1 with description]
- [Link 2 with description]

---

That's all for this week! Reply and let me know what you'd like to see next.

[Signature]`,
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-output-case-study',
    name: 'Case Study',
    description: 'Customer success story template',
    type: 'output',
    contentType: 'Case Study',
    category: 'Written',
    isDefault: true,
    data: {
      contentType: 'Case Study',
      platforms: ['Website'],
      content: `# [Customer Name]: [Headline Result]

## Executive Summary
[2-3 sentences summarizing the challenge and outcome]

## About [Customer Name]
- Industry: [Industry]
- Company Size: [Size]
- Location: [Location]

## The Challenge
[Describe the problem the customer faced]

## The Solution
[How your product/service addressed the challenge]

## Implementation
[Brief overview of the implementation process]

## Results

| Metric | Before | After | Improvement |
|--------|--------|-------|-------------|
| [Metric 1] | [Value] | [Value] | [%] |
| [Metric 2] | [Value] | [Value] | [%] |

## Customer Quote
> "[Testimonial quote from the customer]"
> — [Name], [Title] at [Company]

## Key Takeaways
1. [Takeaway 1]
2. [Takeaway 2]
3. [Takeaway 3]`,
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tpl-output-video-script',
    name: 'Video Script',
    description: 'Script template for educational or promotional videos',
    type: 'output',
    contentType: 'Video',
    category: 'Video',
    isDefault: true,
    data: {
      contentType: 'Video',
      platforms: ['YouTube'],
      content: `# [Video Title]

## Hook (0:00 - 0:15)
[Grab attention immediately - pose a question or make a bold statement]

## Intro (0:15 - 0:30)
"Hey everyone, I'm [Name] and today we're diving into [topic]..."

## Main Content

### Section 1 (0:30 - 2:00)
[First main point with examples]

**B-roll suggestions:** [List visual elements]

### Section 2 (2:00 - 3:30)
[Second main point with examples]

**B-roll suggestions:** [List visual elements]

### Section 3 (3:30 - 5:00)
[Third main point with examples]

**B-roll suggestions:** [List visual elements]

## Recap (5:00 - 5:30)
[Summarize the key points]

## Call to Action (5:30 - 6:00)
"If you found this helpful, hit that subscribe button and leave a comment..."

---
**Thumbnail Text:** [Suggested text]
**Tags:** [Comma-separated tags]`,
    },
    createdBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// =============================================================================
// TEMPLATE CATEGORIES
// =============================================================================

export const TEMPLATE_CATEGORIES = [
  { id: 'marketing', name: 'Marketing', icon: 'megaphone', order: 1 },
  { id: 'content', name: 'Content', icon: 'file-text', order: 2 },
  { id: 'social', name: 'Social Media', icon: 'share-2', order: 3 },
  { id: 'email', name: 'Email', icon: 'mail', order: 4 },
  { id: 'video', name: 'Video', icon: 'video', order: 5 },
  { id: 'events', name: 'Events', icon: 'calendar', order: 6 },
  { id: 'brand', name: 'Brand', icon: 'award', order: 7 },
  { id: 'written', name: 'Written', icon: 'pen-tool', order: 8 },
  { id: 'custom', name: 'Custom', icon: 'plus', order: 99 },
];

// =============================================================================
// SERVICE
// =============================================================================

class TemplatesService {
  private templates: ContentTemplate[] = [...DEFAULT_SEED_TEMPLATES, ...DEFAULT_OUTPUT_TEMPLATES];

  /**
   * Get all templates
   */
  async getAll(type?: 'seed' | 'output'): Promise<ApiResponse<ContentTemplate[]>> {
    try {
      logger.info('content', 'Fetching templates', { type });

      let result = this.templates;
      if (type) {
        result = result.filter((t) => t.type === type);
      }

      return { data: result, error: null };
    } catch (error) {
      logger.error('content', 'Failed to fetch templates', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get template by ID
   */
  async getById(id: string): Promise<ApiResponse<ContentTemplate>> {
    try {
      const template = this.templates.find((t) => t.id === id);
      if (!template) {
        return { data: null, error: new Error('Template not found') };
      }
      return { data: template, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get templates by category
   */
  async getByCategory(category: string): Promise<ApiResponse<ContentTemplate[]>> {
    try {
      const result = this.templates.filter(
        (t) => t.category.toLowerCase() === category.toLowerCase()
      );
      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Create a new template
   */
  async create(template: Omit<ContentTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<ContentTemplate>> {
    try {
      logger.info('content', 'Creating template', { name: template.name });

      const newTemplate: ContentTemplate = {
        ...template,
        id: `tpl-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      this.templates.push(newTemplate);
      return { data: newTemplate, error: null };
    } catch (error) {
      logger.error('content', 'Failed to create template', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Update a template
   */
  async update(id: string, updates: Partial<ContentTemplate>): Promise<ApiResponse<ContentTemplate>> {
    try {
      const index = this.templates.findIndex((t) => t.id === id);
      if (index === -1) {
        return { data: null, error: new Error('Template not found') };
      }

      // Don't allow updating default templates
      if (this.templates[index].isDefault) {
        return { data: null, error: new Error('Cannot modify default templates') };
      }

      this.templates[index] = {
        ...this.templates[index],
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      return { data: this.templates[index], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Delete a template
   */
  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      const index = this.templates.findIndex((t) => t.id === id);
      if (index === -1) {
        return { data: null, error: new Error('Template not found') };
      }

      // Don't allow deleting default templates
      if (this.templates[index].isDefault) {
        return { data: null, error: new Error('Cannot delete default templates') };
      }

      this.templates.splice(index, 1);
      logger.info('content', 'Deleted template', { id });
      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Duplicate a template
   */
  async duplicate(id: string, newName?: string): Promise<ApiResponse<ContentTemplate>> {
    try {
      const template = this.templates.find((t) => t.id === id);
      if (!template) {
        return { data: null, error: new Error('Template not found') };
      }

      const duplicated: ContentTemplate = {
        ...template,
        id: `tpl-${Date.now()}`,
        name: newName || `${template.name} (Copy)`,
        isDefault: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      this.templates.push(duplicated);
      return { data: duplicated, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get default seed templates
   */
  getDefaultSeedTemplates(): ContentTemplate[] {
    return DEFAULT_SEED_TEMPLATES;
  }

  /**
   * Get default output templates
   */
  getDefaultOutputTemplates(): ContentTemplate[] {
    return DEFAULT_OUTPUT_TEMPLATES;
  }
}

export const templatesService = new TemplatesService();
export default templatesService;
